<template>
  <div class="flex flex-col h-full flex-1 bg-gray-800">
    <div class="flex-1 flex flex-col pb-4 overflow-y-auto pt-8">
      <RouterLink
        v-if="account.logo"
        :to="{ name: 'accounts.show', params: { accountId: account.id } }"
      >
        <img :src="account.logo" class="block rounded w-24 mb-8 mx-auto" />
      </RouterLink>
      <h6 class="text-center text-white mb-6">
        {{ account.name }}
      </h6>
      <nav class="flex-1 bg-gray-800" aria-label="Sidebar">
        <template v-for="(item, routeIdx) in navigation">
          <SidebarNavigationGroup
            v-if="'children' in item"
            :key="`root_item_${routeIdx}`"
            :label="item.label"
            :children="item.children"
          />
          <SidebarNavigationLink
            v-else
            :key="`root_item_${routeIdx}`"
            :route="item.route"
            :label="item.label"
            :exact="item.exact"
          />
        </template>
      </nav>
    </div>
  </div>
</template>

<script>
import { accountComputed } from '@/store/helper';
import SidebarNavigationLink from './SidebarNavigationLink.vue';
import SidebarNavigationGroup from './SidebarNavigationGroup.vue';

const getNavigation = (accountId) => [
  {
    label: 'Overview',
    route: { name: 'accounts.show', params: { accountId: accountId } },
    exact: true,
  },
  {
    label: 'Sales Funnel',
    children: [
      {
        label: 'Audiences',
        route: {
          name: 'accounts.accountId.audiences',
          params: { accountId: accountId },
        },
      },
      {
        label: 'Funnels',
        route: {
          name: 'accounts.accountId.funnels',
          params: { accountId: accountId },
        },
      },
      {
        label: 'Prospects',
        route: {
          name: 'accounts.accountId.prospects',
          params: { accountId: accountId },
        },
      },
      {
        label: 'Contacts',
        route: {
          name: 'accounts.accountId.salesFunnel.contacts',
          params: { accountId: accountId },
        },
      },
      {
        label: 'Customers',
        route: {
          name: 'accounts.accountId.customers',
          params: { accountId: accountId },
        },
      },
    ],
  },
  {
    label: 'CRM',
    children: [
      {
        label: 'Orders',
        route: {
          name: 'accounts.accountId.accOrders',
          params: { accountId: accountId },
        },
      },
      {
        label: 'Contact Requests',
        route: {
          name: 'accounts.accountId.contactRequests',
          params: { accountId: accountId },
        },
      },
      {
        label: 'Pipelines',
        route: { name: 'accounts.pipelines', params: { accountId: accountId } },
      },
      {
        label: 'Calendar',
        route: { name: 'accounts.calendar', params: { accountId: accountId } },
      },
      {
        label: 'Tasks',
        route: { name: 'accounts.accountId.tasks', params: { accountId: accountId } },
      },
      {
        label: 'Clearing',
        route: {
          name: 'accounts.accountId.contactMerge',
          params: { accountId: accountId },
        },
      },
      {
        label: 'Contacts',
        route: {
          name: 'accounts.accountId.contacts',
          params: { accountId: accountId },
        },
      },
    ],
  },
  {
    label: 'Marketing Projects',
    children: [
      {
        label: 'Tactics',
        route: {
          name: 'accounts.projectsByProductCategory',
          params: { accountId: accountId, productCategory: 'tactics' },
        },
      },
      {
        label: 'Bulk Messages',
        route: {
          name: 'accounts.projectsByProductCategory',
          params: { accountId: accountId, productCategory: 'bulk-messages' },
        },
      },
      {
        label: 'Posts',
        route: {
          name: 'accounts.projectsByProductCategory',
          params: { accountId: accountId, productCategory: 'posts' },
        },
      },
      {
        label: 'Infrastructures',
        route: {
          name: 'accounts.projectsByProductCategory',
          params: { accountId: accountId, productCategory: 'infrastructures' },
        },
      },
      {
        label: 'Strategy',
        route: {
          name: 'accounts.projectsByProductCategory',
          params: { accountId: accountId, productCategory: 'strategy' },
        },
      },
      {
        label: 'Advertisements',
        route: { name: 'accounts.channels', params: { accountId: accountId } },
      },
    ],
  },
  {
    label: 'Assets',
    children: [
      {
        label: 'Creatives',
        route: { name: 'accounts.accountId.assets', params: { accountId: accountId } },
      },
      {
        label: 'Products',
        route: {
          name: 'accounts.accountId.clientProducts',
          params: { accountId: accountId },
        },
      },
      {
        label: 'Brand',
        route: {
          name: 'accounts.accountId.settings',
          params: { accountId: accountId },
        },
      },
    ],
  },
  {
    label: 'Finance',
    children: [
      {
        label: 'Offers',
        route: { name: 'accounts.accountId.offers', params: { accountId: accountId } },
      },
      {
        label: 'Orders',
        route: { name: 'accounts.orders', params: { accountId: accountId } },
      },
      {
        label: 'Subscriptions',
        route: {
          name: 'accounts.accountSubscriptions',
          params: { accountId: accountId },
        },
      },
      {
        label: 'Invoices',
        route: {
          name: 'accounts.accountId.invoices',
          params: { accountId: accountId },
        },
      },
    ],
  },
  {
    label: 'Campaigns',
    children: [
      {
        label: 'SMS Campaigns',
        route: {
          name: 'accounts.accountId.smsCampaigns',
          params: { accountId: accountId },
        },
      },
    ],
  },
  {
    label: 'Marketing',
    children: [
      {
        label: 'Email Campaigns',
        route: {
          name: 'accounts.accountId.emailCampaigns',
          params: { accountId: accountId },
        },
      },
      {
        label: 'Email Templates',
        route: {
          name: 'accounts.accountId.emailTemplates',
          params: { accountId: accountId },
        },
      },
    ],
  },
  {
    label: 'Discover',
    route: { name: 'accounts.products', params: { accountId: accountId } },
  },
];

export default {
  components: {
    SidebarNavigationLink,
    SidebarNavigationGroup,
  },
  computed: {
    ...accountComputed,
    navigation() {
      return getNavigation(this.$currentAccount.id);
    },
  },
};
</script>
